import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, User, ArrowRight, CheckCircle } from 'lucide-react';
import { useConsultationModal } from '../../context/ConsultationModalContext';
import { useLanguage } from '../../context/LanguageContext';
import useTranslations from '../../hooks/useTranslations';
import { api } from '../../services/api';

const ConsultationModal = () => {
  const { isOpen, source, closeModal } = useConsultationModal();
  const { currentLang } = useLanguage();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const lang = currentLang || 'en';

  // DB-driven translations
  const { t: consT } = useTranslations('consultation', lang);
  const { t: commonT } = useTranslations('common', lang);
  const t = (key, fb) => consT(key, null) || commonT(key, null) || fb || key;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = '';
      setName('');
      setPhone('');
      setSent(false);
      setError('');
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError(t('consultation.error.fillFields', 'Please fill all fields'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/consultation', {
        name: name.trim(),
        phone: phone.trim(),
        source,
        source_page: window.location.href,
      });
      setSent(true);
    } catch (err) {
      setError(t('consultation.error.tryAgain', 'Error. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeModal}
            className="absolute inset-0 bg-[#091828]/70 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md bg-white shadow-2xl overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-[#091828]/40 hover:text-[#091828] transition-colors"
              aria-label={t('consultation.ariaClose', 'Close')}
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            {sent ? (
              /* Success state */
              <div className="py-16 px-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                >
                  <div className="w-16 h-16 rounded-full bg-[#091828]/5 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-[#091828]" strokeWidth={1.5} />
                  </div>
                </motion.div>
                <h3 className="font-display text-xl font-semibold text-[#091828] mb-2">
                  {t('consultation.successTitle', 'Request sent!')}
                </h3>
                <p className="text-[#091828]/55 text-sm leading-relaxed max-w-xs mx-auto">
                  {t('consultation.successMessage', 'We will contact you shortly.')}
                </p>
                <button
                  onClick={closeModal}
                  className="mt-8 text-sm text-[#091828]/70 hover:text-[#091828] underline underline-offset-4 transition-colors"
                >
                  {t('consultation.closeButton', 'Close')}
                </button>
              </div>
            ) : (
              /* Form state */
              <div className="p-8 md:p-10">
                {/* Header */}
                <div className="mb-8">
                  <div className="w-10 h-px bg-[#ebdfcb] mb-4" />
                  <h2 className="font-display text-xl md:text-2xl font-semibold text-[#091828] leading-tight mb-1">
                    {t('consultation.title', 'Book a Consultation')}
                  </h2>
                  <p className="text-[#091828]/45 text-sm leading-relaxed">
                    {t('consultation.subtitle', "Leave your phone number and we'll call you back.")}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-[10px] font-semibold tracking-[0.16em] uppercase text-[#091828]/50 mb-2">
                      {t('consultation.nameLabel', 'Your Name')}
                    </label>
                    <div className="relative">
                      <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#091828]/25" strokeWidth={1.5} />
                      <input
                        ref={inputRef}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('consultation.namePlaceholder', 'e.g. John Doe')}
                        className="w-full pl-10 pr-4 py-3 border border-[#091828]/15 bg-white text-[#091828] placeholder-[#091828]/25 text-sm focus:ring-0 focus:border-[#091828]/40 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-[10px] font-semibold tracking-[0.16em] uppercase text-[#091828]/50 mb-2">
                      {t('consultation.phoneLabel', 'Phone')}
                    </label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#091828]/25" strokeWidth={1.5} />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+40 7xx xxx xxx"
                        className="w-full pl-10 pr-4 py-3 border border-[#091828]/15 bg-white text-[#091828] placeholder-[#091828]/25 text-sm focus:ring-0 focus:border-[#091828]/40 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Error */}
                  {error && (
                    <p className="text-red-500 text-xs">{error}</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#091828] hover:bg-[#0D1626] text-white font-semibold text-sm uppercase tracking-[0.12em] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        {t('consultation.sendButton', 'Send')}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-[#091828]/30 text-[10px] text-center leading-relaxed">
                    {t('consultation.disclaimer', 'Your data is safe. We never share it with anyone.')}
                  </p>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConsultationModal;
